import React, { useEffect, useRef, useState } from 'react'

interface VideoSource {
  src: string
  type: string
}
interface Video {
  id: string
  name: string
  description: string
  duration: number
  poster: string
  sources: VideoSource[]
}

interface VideoComponentProps {
  videoId?: string
  playlistId?: string
  autoplay?: boolean
  inline?: boolean
  policy?: string
  account: string
  shouldGrow?: boolean
}

const loadScript = (id: string) => {
  const SCRIPT_URL = `//players.brightcove.net/${id}/Nr5YMm2WRW_default/index.min.js`
  if (document.querySelector(`script[src="${SCRIPT_URL}"]`)) return
  const script = document.createElement('script')
  script.src = `${SCRIPT_URL}?t=${Date.now()}`
  script.async = true
  document.body.appendChild(script)
}

const formatText = (txt: string) => {
  return txt
    .replace(/\(TM\)/g, '<sup>&#8482;</sup>') // TM
    .replace(/\(r\)/g, '<sup>&#174;</sup>') // REGISTERED SIGN
}

const formatTime = (time: number) => {
  const timeInSeconds = Math.floor(time / 1000)
  const minutes = Math.floor(timeInSeconds / 60)
  let seconds = Math.floor(timeInSeconds % 60)

  return `(${minutes}:${seconds < 10 ? '0' + seconds : seconds})`
}

const scrollTo = (el: HTMLVideoElement) => {
  let top = window.pageYOffset + el.getBoundingClientRect().top
  if (el.scrollHeight < window.innerHeight) {
    top = window.innerHeight / 2 - el.scrollHeight / 2
  }
  window.scroll({
    behavior: 'smooth',
    top: top,
  })
}

const addPopupMenuListeners = () => {
  const playbackButton = document.querySelector('.playback-button')
  const qualityButton = document.querySelector('.quality-button')
  const playbackMenu = document.querySelector('.playback-menu')
  const qualityMenu = document.querySelector('.quality-menu')

  if (playbackButton && playbackMenu) {
    playbackButton.addEventListener('mouseenter', () => {
      playbackMenu.classList.add('vjs-menu-popup-playback')
    })
    playbackMenu.addEventListener('mouseleave', () => {
      playbackMenu.classList.remove('vjs-menu-popup-playback')
    })
  }

  if (qualityButton && qualityMenu) {
    qualityButton.addEventListener('mouseenter', () => {
      qualityMenu.classList.add('vjs-menu-popup-quality')
    })
    qualityMenu.addEventListener('mouseleave', () => {
      qualityMenu.classList.remove('vjs-menu-popup-quality')
    })
    qualityButton.addEventListener('click', () => {
      qualityMenu.classList.remove('vjs-lock-showing')
    })
  }
}

const VideoComponent: React.FC<VideoComponentProps> = ({
  videoId,
  playlistId,
  autoplay = false,
  inline = false,
  policy,
  account,
  shouldGrow,
}) => {
  const videoRef = useRef<(HTMLVideoElement | null)[]>([])
  const [videos, setVideos] = useState<Video[]>([])

  const addPlayPauseToText = () => {
    videos.forEach(video => {
      const videoText = document.getElementById(`${video.id}-text-target`)
      if (videoText) {
        videoText.addEventListener('click', () => {
          const videoEl = videoText.previousElementSibling?.querySelector('video')
          if (videoEl && videoEl.classList.contains('currently-playing')) {
            videoEl.pause()
          } else {
            videoEl?.play()
          }
        })
      }
    })
  }

  useEffect(() => {
    const fetchVideoInfo = async () => {
      const resourceUrl = videoId ? `videos/${videoId}` : `playlists/${playlistId}`
      const API_URL = `https://edge.api.brightcove.com/playback/v1/accounts/${account}`
      try {
        const response = await fetch(`${API_URL}/${resourceUrl}`, {
          headers: {
            Accept: 'application/json;pk=' + policy,
          },
        })

        if (!response.ok) throw new Error('Failed to fetch video data')

        const data = await response.json()
        if (data.videos) {
          setVideos(data.videos)
        } else {
          setVideos([
            {
              id: data.id,
              name: data.name,
              description: data.description,
              duration: data.duration,
              poster: data.poster,
              sources: data.sources[0].src,
            },
          ])
        }
      } catch (error) {
        console.error('Error loading video:', error)
      }
    }
    fetchVideoInfo()
    addPlayPauseToText()
    addPopupMenuListeners()
    loadScript(account)
  }, [videoId, playlistId])

  const handleVideoEvents = (index: number) => {
    const player = videoRef.current[index]
    if (!player) return
    player.addEventListener('play', () => {
      const posterElements = player.parentElement?.querySelectorAll('.vjs-poster')

      posterElements?.forEach(el => {
        ;(el as HTMLElement).style.display = 'none'
      })
      document
        .getElementById(`${player.getAttribute('data-video-id')}-vc-video-container`)
        ?.classList.remove('inline')
      scrollTo(player)
    })
    player.addEventListener('ended', () => {
      const posterElements = player.parentElement?.querySelectorAll('.vjs-poster')

      posterElements?.forEach(el => {
        ;(el as HTMLElement).style.display = 'block'
      })
    })
  }
  return (
    <div className={`video-container ty-message${shouldGrow ? ' u-flexGrow' : ''}`}>
      {videos.length > 0 &&
        videos.map((video, index) => {
          return (
            <div
              key={video.id}
              id={`${video.id}-vc-video-container`}
              className={`vc-video-container ${inline ? 'inline' : ''}`}>
              <div className='sixteen-by-nine-container'>
                <video
                  ref={el => {
                    if (el) videoRef.current[index] = el
                  }}
                  className={`video-js ${inline ? 'inline' : ''} `}
                  controls={videos.length > 0}
                  autoPlay={autoplay}
                  data-video-id={video.id}
                  data-account={account}
                  data-player='Nr5YMm2WRW'
                  data-embed='default'
                  data-application-id
                  data-analytics='video'
                  data-analytics-video-id={video.id}
                  poster={video.poster}
                  id={video.id}
                  onLoadedMetadata={() => handleVideoEvents(index)}>
                  {video.sources.map((source, idx) => (
                    <source key={idx} src={source.src} type={source.type} />
                  ))}
                  Your browser does not support the video tag
                </video>
              </div>
              <div id={`${video.id}-text-target`} className='video-description-container'>
                <h4 className='video-name' data-analytics='video-title'>
                  {formatText(video.name)}
                </h4>
                <p data-analytics='video-description'>
                  <span className='video-long-description'>{formatText(video.description)}</span>
                  <span className='video-duration'>{formatTime(video.duration)}</span>
                </p>
              </div>
            </div>
          )
        })}
    </div>
  )
}

export default VideoComponent
